import { useState } from 'react';
import { anioMesNegocio } from '@tanteo/compartido';
import { datosExporteProductos, datosExporteTerceros, datosExporteVentas } from '../lib/api-exportes';
import { useApp } from '../lib/contexto';
import { trocear } from '../lib/partes';
import { Boton, CampoLista, MensajeError, MensajeExito, Tarjeta, TextoAyuda } from '../ui/base';
import { descargarCSV } from '../ui/base/descargar';

/**
 * CSVs para el contador con las columnas de las plantillas de Siigo Nube (docs/research/mercado-proveedores.md §A3).
 * Siigo acepta 500 filas por archivo; con más, cada parte tiene su botón: el navegador frena las descargas
 * seguidas que la persona no pidió, y la segunda parte podría no bajar.
 */
const LIMITE_SIIGO = 500;

type Filas = (string | number | null)[][];
interface Partes {
  titulo: string;
  base: string;
  cabecera: string[];
  trozos: Filas[];
  total: number;
  bajadas: number[];
}

export function Exportes() {
  const { negocio } = useApp();
  const hoy = anioMesNegocio();
  const [anio, setAnio] = useState(hoy.anio);
  const [mes, setMes] = useState(hoy.mes);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [partes, setPartes] = useState<Partes | null>(null);

  if (!negocio) return null;

  function trocearYDescargar(titulo: string, base: string, cabecera: string[], filas: Filas) {
    if (filas.length === 0) {
      setAviso('No hay datos para exportar.');
      return;
    }
    if (filas.length <= LIMITE_SIIGO) {
      descargarCSV(`${base}.csv`, cabecera, filas);
      setAviso(`Exportado: ${filas.length} fila(s).`);
      return;
    }
    setPartes({ titulo, base, cabecera, trozos: trocear(filas, LIMITE_SIIGO), total: filas.length, bajadas: [] });
  }

  function bajarParte(p: Partes, i: number) {
    descargarCSV(`${p.base}-parte${i + 1}.csv`, p.cabecera, p.trozos[i]!);
    setPartes({ ...p, bajadas: p.bajadas.includes(i) ? p.bajadas : [...p.bajadas, i] });
  }

  /** Único camino de descarga: un fallo del servidor se muestra en la pantalla. */
  async function exportar(titulo: string, base: string, cabecera: string[], obtener: () => Promise<Filas>) {
    setAviso('');
    setError('');
    setPartes(null);
    try {
      trocearYDescargar(titulo, base, cabecera, await obtener());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos generar el archivo.');
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-titulo font-extrabold">Exportes para el contador</h1>
      <p className="text-muted text-contenido">
        Archivos CSV con la estructura de las plantillas de importación de Siigo Nube. El contador los revisa y los sube
        desde Configuración → Importación en Siigo. (Verificar contra la plantilla oficial vigente antes del primer uso.)
      </p>
      <MensajeExito>{aviso}</MensajeExito>
      <MensajeError caja e2e="exporte-error">{error}</MensajeError>
      {partes && (
        <Tarjeta data-e2e="exporte-partes">
          <p className="mb-3 font-semibold">
            {partes.titulo}: {partes.total} filas, en {partes.trozos.length} archivos (Siigo acepta hasta 500)
          </p>
          <div className="flex flex-wrap gap-2">
            {partes.trozos.map((_, i) => (
              <Boton key={i} variante="contorno" data-e2e={`exportar-parte-${i + 1}`} onClick={() => bajarParte(partes, i)}>
                {partes.bajadas.includes(i) ? '✓ ' : ''}Descargar parte {i + 1} de {partes.trozos.length}
              </Boton>
            ))}
          </div>
        </Tarjeta>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Tarjeta>
          <h2 className="mb-2 font-extrabold">Productos</h2>
          <TextoAyuda>Catálogo con SKU, nombre, categoría, precio e impuesto.</TextoAyuda>
          <Boton
            data-e2e="exportar-productos"
            onClick={() =>
              void exportar('Productos', 'siigo-productos', ['Codigo', 'Nombre', 'Linea', 'Precio', 'Impuesto'], () =>
                datosExporteProductos(negocio.negocioId)
              )
            }
          >
            Descargar productos
          </Boton>
        </Tarjeta>

        <Tarjeta>
          <h2 className="mb-2 font-extrabold">Terceros</h2>
          <TextoAyuda>Clientes y proveedores con tipo y número de identificación (códigos DIAN).</TextoAyuda>
          <Boton
            data-e2e="exportar-terceros"
            onClick={() =>
              void exportar(
                'Terceros',
                'siigo-terceros',
                ['Tipo identificacion', 'Numero identificacion', 'DV', 'Nombre', 'Telefono', 'Correo', 'Clasificacion', 'Ciudad DANE'],
                () => datosExporteTerceros(negocio.negocioId)
              )
            }
          >
            Descargar terceros
          </Boton>
        </Tarjeta>

        <Tarjeta>
          <h2 className="mb-2 font-extrabold">Ventas del mes</h2>
          <div className="mb-3 flex gap-2">
            <CampoLista value={mes} onChange={(e) => {
                setMes(Number(e.target.value));
                setPartes(null);
              }} aria-label="Mes">
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </CampoLista>
            <CampoLista className="num" value={anio} onChange={(e) => {
                setAnio(Number(e.target.value));
                setPartes(null);
              }} aria-label="Año">
              {[hoy.anio, hoy.anio - 1].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </CampoLista>
          </div>
          <Boton
            data-e2e="exportar-ventas"
            onClick={() =>
              void exportar(
                `Ventas de ${mes}/${anio}`,
                `siigo-ventas-${anio}-${String(mes).padStart(2, '0')}`,
                ['Consecutivo', 'Fecha', 'Identificacion tercero', 'Metodo pago', 'Medio pago DIAN', 'Subtotal', 'Impuesto', 'Total', 'Estado'],
                () => datosExporteVentas(negocio.negocioId, anio, mes)
              )
            }
          >
            Descargar ventas
          </Boton>
        </Tarjeta>
      </div>
    </div>
  );
}
